import { Link } from "react-router-dom";
import { SEGMENTS, SEGMENT_KEYS } from "../services/constants";
import { compactNumber, currencySymbol, isOnline, regionFlag,
  relativeTime } from "../utils/format";
import "./BuyerCard.css";

const URGENCY_META = {
  now:   { label: "URGENT", color: "var(--danger)" },
  "24h": { label: "24H",    color: "var(--accent)" },
  week:  { label: "1W",     color: "var(--text-muted)" },
  month: { label: "1MO",    color: "var(--text-dim)" },
};

// Buyer card — top need + max rate + budget surfaced for sellers
// browsing the demand side. Same shimmer/halo idiom as SellerCard,
// inverted accent: green-blue instead of gold.
export default function BuyerCard({ buyer }) {
  const online = isOnline(buyer.lastActiveAt);
  const initial = (buyer.displayName || buyer.handle || "?")[0].toUpperCase();

  // Find the largest single need to feature
  const topNeed = SEGMENT_KEYS
      .map((k) => ({
        key: k,
        ...buyer.needs?.[k],
        meta: SEGMENTS.find((s) => s.key === k),
      }))
      .filter((n) => Number(n.quantity) > 0)
      .sort((a, b) => (Number(b.quantity) || 0) - (Number(a.quantity) || 0))[0];

  const symbol = currencySymbol(buyer.currency || "INR");

  return (
    <Link to={`/buyer/${buyer.handle}`}
      className={`bc-card bc-tier-${buyer.tier || "0"}`}>
      <div className="bc-shine" aria-hidden="true" />
      <div className="bc-halo" aria-hidden="true" />

      <div className="bc-top">
        <div className="bc-avatar">
          {initial}
          {online && <span className="bc-online-dot" />}
        </div>
        <div className="bc-headline">
          <div className="bc-line1">
            <span className="bc-name">{buyer.displayName || buyer.handle}</span>
            <span className="bc-buyer-tag">BUYER</span>
          </div>
          <div className="bc-line2">
            <span className="bc-handle">@{buyer.handle}</span>
            <span className="bc-dot">·</span>
            <span className={`bc-game ${buyer.game === "BGMI" ? "bgmi" : "pubgm"}`}>
              {buyer.game}
            </span>
            {buyer.city && <>
              <span className="bc-dot">·</span>
              <span>{buyer.city}</span>
            </>}
          </div>
        </div>
        <div className="bc-flag">{regionFlag(buyer.region, buyer.countryCode)}</div>
      </div>

      {/* Top need */}
      {topNeed ? (
        <div className="bc-need">
          <div className="bc-need-line">
            <span className={`bc-seg-badge ${topNeed.meta?.cssClass}`}>
              {topNeed.meta?.label}
            </span>
            <span className="bc-need-qty">
              Need <strong>{compactNumber(topNeed.quantity)}</strong>
            </span>
            <span className="bc-need-rate">
              @ ≤ <strong>{symbol}{Number(topNeed.maxRate || 0).toFixed(topNeed.maxRate >= 100 ? 0 : 2)}</strong>
              {topNeed.meta?.key === "mix" || topNeed.meta?.key === "premium" ? "/k" : ""}
            </span>
            {URGENCY_META[topNeed.urgency] && (
              <span className="bc-urgency" style={{ color: URGENCY_META[topNeed.urgency].color, borderColor: URGENCY_META[topNeed.urgency].color }}>
                {URGENCY_META[topNeed.urgency].label}
              </span>
            )}
          </div>
          {buyer.battleContext && (
            <div className="bc-battle">⚔ {buyer.battleContext}</div>
          )}
        </div>
      ) : (
        <div className="bc-need dim">No specific needs posted yet</div>
      )}

      <div className="bc-bottom">
        <div className="bc-budget">
          {buyer.budgetINR ? (
            <>
              <div className="bc-budget-num">₹{compactNumber(buyer.budgetINR)}</div>
              <div className="bc-tinylabel">TOTAL BUDGET</div>
            </>
          ) : (
            <>
              <div className="bc-budget-num bc-budget-open">Open</div>
              <div className="bc-tinylabel">BUDGET</div>
            </>
          )}
        </div>
        <div className="bc-meta">
          {online ? (
            <span className="bc-online-text">● ONLINE NOW</span>
          ) : (
            <span className="dim">
              Last seen {relativeTime(buyer.lastActiveAt)}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}
